import React, { useContext } from 'react'
import { Trans, t } from '@lingui/macro'

import { ManageCampaignContext } from '../../ManageCampaignPage'

import { notification } from '../../../../components/common/notifications/Notifications'
import NewActionHeader from '../new-action-header/NewActionHeader'
import {
  ButtonPrimary,
  ButtonLight,
  Switch,
  Input
} from 'front-end/src/components'
import { linkBuilder } from '../../../../../../shared/linkBuilder'
import { navigateTo } from '../../../../app/util/appHistory'
import IconArrowLeft from '../../../../components/common/icon/IconArrowLeft'
import IconArrowRight from '../../../../components/common/icon/IconArrowRight'
import { FormRow } from '../../../../components/common/form/Form'
import Label from '../../../../components/common/form/label/Label'
import Textarea from '../../../../components/common/form/textarea/Textarea'
import DateTimePickers from '../../../../components/common/date-time-pickers/DateTimePickers'

import {
  ICampaignShareComponentProps,
  ICampaignShareComponentState,
  STEPS
} from './CampaignShareComponent'
import {
  isCampaignFixed,
  isCampaignImmediate,
  isCampaignRelative
} from '../../utils'
import { TemplatingInfoBar } from '../TemplatingInfoBar'
import { SenderSelect } from '../SenderSelect'
import dayjs, { Dayjs } from 'shared/dayjs'
import TimePicker from '../../../../components/common/date-time-pickers/TimePicker'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

const CampaignShareStepSettings = (
  props: ICampaignShareComponentState & {
    onStateUpdate: (updatedState: Partial<ICampaignShareComponentState>) => void
    selectedItemsIds: number[]
  } & ICampaignShareComponentProps
) => {
  const { campaign } = useContext(ManageCampaignContext)

  const {
    type,
    message,
    onStateUpdate,
    toggleCompletionDate,
    sendShareEmail,
    completionDate,
    relativeCompletion,
    resetEnrolments,
    title,
    itemList,
    email_identity_id
  } = props

  const isSettingsValid = () => {
    if (isCampaignRelative(campaign) && toggleCompletionDate) {
      if (
        !relativeCompletion.day &&
        type !== CampaignActionTypeEnum.SURVEY_SHARE_ACTION
      ) {
        notification.warning(t`Please fill in target completion day`)
        return false
      }
      if (
        !relativeCompletion.time &&
        type !== CampaignActionTypeEnum.SURVEY_SHARE_ACTION
      ) {
        notification.warning(t`Please fill in target completion time`)
        return false
      }
    }

    return true
  }

  const getDayjsDate = (date: string | Dayjs | null) => {
    if (dayjs.isDayjs(date)) {
      return completionDate as Dayjs
    }
    if (date === null) {
      return null
    }
    return dayjs(completionDate)
  }

  return (
    <>
      <NewActionHeader
        title={title}
        steps={STEPS}
        currentStep={STEPS.SETTINGS}
        prevButton={
          <ButtonLight
            onClick={() => {
              navigateTo(linkBuilder.campaign(campaign.id))
            }}
            leftIcon={<IconArrowLeft />}
          >
            <Trans>Back</Trans>
          </ButtonLight>
        }
        nextButton={
          <ButtonPrimary
            onClick={() => onStateUpdate({ step: STEPS.SCHEDULE })}
            rightIcon={<IconArrowRight />}
          >
            <Trans>Schedule</Trans>
          </ButtonPrimary>
        }
      />

      <div className="CampaignShareComponent__wrapper">
        {itemList}

        <h3>
          <Trans>Additional settings</Trans>
        </h3>

        <SenderSelect
          value={email_identity_id}
          onChange={(v) => onStateUpdate({ email_identity_id: v })}
        />

        <FormRow>
          <Label>
            <Trans>Email message</Trans> (<Trans>optional</Trans>)
          </Label>
          <Textarea
            fullWidth
            rows={6}
            placeholder={t`Optional: include a message...`}
            onChange={(val) => onStateUpdate({ message: val })}
            value={message}
          />
          <TemplatingInfoBar />
        </FormRow>

        {type !== CampaignActionTypeEnum.SURVEY_SHARE_ACTION && (
          <FormRow className="CampaignShareComponent__checkbox">
            <label>
              <Trans>Set a target completion date?</Trans>
            </label>
            <Switch
              onChange={() =>
                onStateUpdate({ toggleCompletionDate: !toggleCompletionDate })
              }
              isChecked={toggleCompletionDate}
            />
          </FormRow>
        )}
        {toggleCompletionDate &&
          (isCampaignFixed(campaign) || isCampaignImmediate(campaign) ? (
            <FormRow>
              <DateTimePickers
                disablePastDays
                value={getDayjsDate(completionDate)}
                onChange={(value: Dayjs | null) => {
                  onStateUpdate({ completionDate: value })
                }}
              />
            </FormRow>
          ) : (
            <FormRow className="CampaignShareComponent__relativeTarget">
              <Trans>In</Trans>
              <Input
                type="number"
                min="0"
                max="999"
                width="60px"
                variant="filled"
                m="0 4px"
                value={relativeCompletion.day || ('' as any)}
                onChange={(event) =>
                  onStateUpdate({
                    relativeCompletion: {
                      ...relativeCompletion,
                      day: parseInt(event.currentTarget.value, 10)
                    }
                  })
                }
              />
              <Trans>days, at</Trans>
              <TimePicker
                value={
                  relativeCompletion.time
                    ? relativeCompletion.time.substr(0, 5)
                    : ''
                }
                placeholder={' '}
                onChangeHandler={(value: string) =>
                  onStateUpdate({
                    relativeCompletion: {
                      ...relativeCompletion,
                      time: `${value}:00`
                    }
                  })
                }
              />
              <br />
            </FormRow>
          ))}

        <FormRow className="CampaignShareComponent__checkbox">
          <label>
            <Trans>Send share email?</Trans>
          </label>
          <Switch
            onChange={() => onStateUpdate({ sendShareEmail: !sendShareEmail })}
            isChecked={sendShareEmail}
          />
        </FormRow>

        {type !== CampaignActionTypeEnum.SURVEY_SHARE_ACTION && (
          <FormRow className="CampaignShareComponent__checkbox">
            {type === CampaignActionTypeEnum.TOPIC_SHARE_ACTION && (
              <label>
                <Trans>Reset progress</Trans>
                <div className="form-hint">
                  <Trans>
                    When disabled, sharing will create new enrolments for all
                    selected users and will aim to maintain their progress,
                    while questions with incorrect answers and failed resources
                    will be available again. <br />
                    When enabled, new clear enrolments will be created and users
                    will need to start again.
                  </Trans>
                </div>
              </label>
            )}
            {type === CampaignActionTypeEnum.PATHWAY_SHARE_ACTION && (
              <label>
                <Trans>Reset progress</Trans>
                <div className="form-hint">
                  <Trans>
                    When disabled, sharing will create new enrolments for all
                    selected users and will aim to maintain their progress,
                    while questions with incorrect answers and failed resources
                    will be available again. <br />
                    When enabled, new clear enrolments will be created and users
                    will need to start again.
                  </Trans>
                </div>
              </label>
            )}
            {type === CampaignActionTypeEnum.CERTIFICATION_SHARE_ACTION && (
              <label>
                <Trans>Reset progress</Trans>
                <div className="form-hint">
                  <Trans>
                    When disabled, sharing will create new enrolments for all
                    selected users and will aim to maintain their progress,
                    while questions with incorrect answers and failed resources
                    will be available again. <br />
                    When enabled, new clear enrolments will be created and users
                    will need to start again.
                  </Trans>
                </div>
              </label>
            )}
            <Switch
              onChange={() =>
                onStateUpdate({ resetEnrolments: !resetEnrolments })
              }
              isChecked={resetEnrolments}
            />
          </FormRow>
        )}

        <FormRow className="CampaignShareComponent__submit">
          <ButtonPrimary
            onClick={() => {
              if (isSettingsValid()) {
                onStateUpdate({ step: STEPS.SCHEDULE })
              }
            }}
            rightIcon={<IconArrowRight />}
          >
            <Trans>Schedule</Trans>
          </ButtonPrimary>
        </FormRow>
      </div>
    </>
  )
}

export default CampaignShareStepSettings
